import { useState, useEffect, useCallback, useRef } from 'react';
import { ANIMATION_TIMING } from '../styles/animations';

/**
 * Custom hook to manage the step-by-step animation sequence for revealing results
 * Controls the timing and state for 4th, 3rd, 2nd, and 1st place reveals
 * Optimized to prevent memory leaks by properly cleaning up timeouts
 */
const useAnimationSequence = () => {
  // Animation states for each place
  const [showFourthPlace, setShowFourthPlace] = useState(false);
  const [showThirdPlace, setShowThirdPlace] = useState(false);
  const [showSecondPlace, setShowSecondPlace] = useState(false);
  const [showFirstPlace, setShowFirstPlace] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  
  // Animation sequence completed flag
  const [isComplete, setIsComplete] = useState(false);
  
  // Animation in progress flag
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Use refs to store timeout IDs for proper cleanup
  const timeoutsRef = useRef([]);
  
  // Helper function to clear all timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutsRef.current = [];
  }, []);
  
  // Cleanup timeouts when component unmounts
  useEffect(() => {
    return () => clearAllTimeouts();
  }, [clearAllTimeouts]);
  
  // Function to start the animation sequence
  const startAnimation = useCallback(() => {
    // Clear any existing timeouts to prevent memory leaks
    clearAllTimeouts();
    
    // Reset all states
    setShowFourthPlace(false);
    setShowThirdPlace(false);
    setShowSecondPlace(false);
    setShowFirstPlace(false);
    setShowCelebration(false);
    setIsComplete(false);
    
    // Set animating flag
    setIsAnimating(true);
    
    // Initial delay before starting the sequence
    const timeout1 = setTimeout(() => {
      // Reveal 4th place
      setShowFourthPlace(true);
      
      // Reveal 3rd place
      const timeout2 = setTimeout(() => {
        setShowThirdPlace(true);
        
        // Reveal 1st and 2nd place at the same time
        const timeout3 = setTimeout(() => {
          setShowFirstPlace(true);
          setShowSecondPlace(true);
          setShowCelebration(true);
          
          // End celebration after duration
          const timeout4 = setTimeout(() => {
            setIsComplete(true);
            setIsAnimating(false);
          }, ANIMATION_TIMING.CELEBRATION_DURATION);
          
          timeoutsRef.current.push(timeout4);
        }, ANIMATION_TIMING.FIRST_PLACE_DELAY);
        
        timeoutsRef.current.push(timeout3);
      }, ANIMATION_TIMING.THIRD_PLACE_DELAY);
      
      timeoutsRef.current.push(timeout2);
    }, ANIMATION_TIMING.INITIAL_DELAY);
    
    timeoutsRef.current.push(timeout1);
  }, [clearAllTimeouts]);
  
  // Function to reset the animation sequence
  const resetAnimation = useCallback(() => {
    // Clear any existing timeouts
    clearAllTimeouts();
    
    setShowFourthPlace(false);
    setShowThirdPlace(false);
    setShowSecondPlace(false);
    setShowFirstPlace(false);
    setShowCelebration(false);
    setIsComplete(false);
    setIsAnimating(false);
  }, [clearAllTimeouts]);
  
  // Function to skip to the end of the animation
  const skipToEnd = useCallback(() => {
    // Clear any existing timeouts
    clearAllTimeouts();
    
    setShowFourthPlace(true);
    setShowThirdPlace(true);
    setShowSecondPlace(true);
    setShowFirstPlace(true);
    setShowCelebration(false);
    setIsComplete(true);
    setIsAnimating(false);
  }, [clearAllTimeouts]);
  
  // Calculate total animation duration
  const totalDuration = 
    ANIMATION_TIMING.INITIAL_DELAY +
    ANIMATION_TIMING.FOURTH_PLACE_DELAY +
    ANIMATION_TIMING.THIRD_PLACE_DELAY +
    ANIMATION_TIMING.SECOND_PLACE_DELAY +
    ANIMATION_TIMING.FIRST_PLACE_DELAY +
    ANIMATION_TIMING.CELEBRATION_DURATION;
  
  return {
    // Animation states
    showFourthPlace,
    showThirdPlace,
    showSecondPlace,
    showFirstPlace,
    showCelebration,
    isComplete,
    isAnimating,
    
    // Control functions
    startAnimation,
    resetAnimation,
    skipToEnd,
    
    // Timing information
    totalDuration,
    timings: ANIMATION_TIMING
  };
};

export default useAnimationSequence;